import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import { ProjectsProvider } from '../provider';
import { useTranslation } from 'react-i18next';
import { FilterSelectNative } from '@/components/custom/filter';
import { useCourse } from 'src/app/hooks/useCourse';
import Header from '@/components/custom/header/header';
import TabNavBar from '../TabNavBar';
import { FilterSelectMulti } from '@/components/custom/filter/filter-select-multi';
import usePermission from '@/app/hooks/usePermission';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import GroupProjectCard from '../GroupProjects/GroupProjectCard';
import toast from 'react-hot-toast';
import useAuthorityLevel from '@/app/hooks/useAuthorityLevel';
import Error404 from 'src/app/components/Error404';
import Select from 'react-select';
import { Dashboard, DashboardContent } from '@/layouts/dashboard';

export function IndividualProjects(props) {
  return (
    <ProjectsProvider>
      <ScreenContentIndividualProjects {...props} />
    </ProjectsProvider>
  );
}

export function ScreenContentIndividualProjects(props) {
  const { uri } = props;

  const { t } = useTranslation();
  const { courses, getSimpleCourses } = useCourse();
  const [classrooms, setClassrooms] = useState([]);
  const [individualProjects, setIndividualProjects] = useState([]);
  const [filteredIndividualProjects, setFilteredIndividualProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const client = useClient();
  const authorityLevel = useAuthorityLevel();

  const filters = useFormik({
    initialValues: {
      course: 'blank',
      classroom: { value: 'blank', label: t('filter.blankLabelAll') },
      approvedProjects: false,
      projectsInProgress: false
    }
  });

  const otherFilters = [
    {
      label: t('metrics.approvedProjects'),
      value: filters.values.approvedProjects,
      onChange: () => {
        filters.setFieldValue('projectsInProgress', false);
        filters.setFieldValue('approvedProjects', !filters.values.approvedProjects);
      }
    },
    {
      label: t('metrics.projectsInProgress'),
      value: filters.values.projectsInProgress,
      onChange: () => {
        filters.setFieldValue('approvedProjects', false);
        filters.setFieldValue('projectsInProgress', !filters.values.projectsInProgress);
      }
    }
  ];

  const getClassrooms = async () => {
    if (filters.values?.course && filters.values.course !== 'blank') {
      const { data } = await client.fetch(`classrooms?simple=true&filter[course_id]=${filters.values.course}`);
      setClassrooms(data);
    }
  };

  const getIndividualProjects = async () => {
    setLoading(true);
    const { data, error } = await client.fetch('project_groups?filter[project_model]=individual');
    if (error) {
      toast.error('Erro ao buscar Projetos Individuais.');
    } else {
      setIndividualProjects(data);
      setLoading(false);
    }
  };

  const deleteIndividualProject = async id => {
    setLoading(true);
    const { error } = await client.delete(['project_groups', id]);
    if (error) {
      toast.error(t('toast.errorDeleteProjectGroup'));
    } else {
      toast.success(t('toast.successDeleteProjectGroup'));
      setFilteredIndividualProjects(filteredIndividualProjects.filter(p => p.id !== id));
      setLoading(false);
    }
  };

  useEffect(() => {
    getSimpleCourses();
    getIndividualProjects();
  }, []);

  useEffect(() => {
    getClassrooms();
    filters.setFieldValue('classroom', { value: 'blank', label: t('filter.blankLabelAll') });
  }, [filters.values.course]);

  useEffect(() => {
    let newIndividualProjects = individualProjects;
    if (filters.values.course !== 'blank') {
      newIndividualProjects = individualProjects.filter(u => u.projects.some(p => p['projectable-type'] === 'Course' && p['projectable-id'] === +filters.values.course));
    }
    if (filters.values.classroom.value !== 'blank') {
      newIndividualProjects = individualProjects.filter(u => u.projects.some(p => p['projectable-type'] === 'Classroom' && p['projectable-id'] === +filters.values.classroom.value));
    }
    if (filters.values.approvedProjects === true) {
      newIndividualProjects = newIndividualProjects.filter(u => u.projects.every(p => p.approved === true));
    }
    if (filters.values.projectsInProgress === true) {
      newIndividualProjects = newIndividualProjects.filter(u => u.projects.some(p => p.approved === null));
    }
    setFilteredIndividualProjects(newIndividualProjects);
  }, [filters.values, individualProjects]);

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: uri,
        name: 'Projetos',
        isActive: true
      }
    ]
  };

  return (
    <ProjectsProvider>
      {authorityLevel !== 'student' ? (
        <Dashboard>
          <Header />

          {usePermission('Visualizar lista de projetos', 'Projetos') && (
            <DashboardContent
              variant="plain"
              breadcrumbs={breadcrumbs}
            >
              <TabNavBar selectedTab="individuais" />

              <div className="filter-bar">
                <div className="filter-bar__inner">
                  <FilterSelectNative
                    blankLabel={t('filter.blankLabelAll')}
                    label={t('filter.labelSelectCourse')}
                    value={filters.values.course}
                    onChange={e => filters.setFieldValue('course', e.target.value)}
                    options={courses.map(o => ({
                      id: o.id,
                      name: o.title
                    }))}
                  />
                  <div>
                    <label className="form__label">{t('filter.labelSelectClassroom')}</label>
                    <Select
                      value={filters.values.classroom}
                      openMenuOnFocus
                      options={[
                        { value: 'blank', label: t('filter.blankLabelAll') },
                        ...classrooms?.map(item => {
                          return { value: item.id, label: item?.institution?.name + ' - ' + item?.course?.title + ' - ' + item.title };
                        })
                      ]}
                      className="react-multi-select filter-bar__multi-select"
                      classNamePrefix="react-multi-select"
                      placeholder={loading ? t('loader.loading2') : t('filter.blankLabelAll')}
                      noOptionsMessage={() => 'Sem opções'}
                      components={{
                        IndicatorSeparator: () => null,
                        ClearIndicator: () => null
                      }}
                      onChange={e => filters.setFieldValue('classroom', e)}
                    />
                  </div>
                  {/* <FilterSelectNative
                    blankLabel={t('filter.blankLabelAll')}
                    label={t('filter.labelSelectClassroom')}
                    value={filters.values.classroom}
                    onChange={e => filters.setFieldValue('classroom', e.target.value)}
                    options={classrooms.map(o => ({
                      id: o.id,
                      name: o.title
                    }))}
                    disabled={filters.values.course === 'blank'}
                  /> */}
                  <FilterSelectMulti
                    data={otherFilters}
                    title="Status"
                  />
                </div>

                {usePermission('Criar projetos', 'Projetos') && (
                  <Link
                    to="/projetos/criar/passo/curso-turma"
                    className="btn btn--wide btn--primary"
                  >
                    {t('projects.newProjects')}
                  </Link>
                )}
              </div>

              {loading && <Loader />}

              {filteredIndividualProjects.length === 0 && !loading && (
                <EmptyState
                  type="project"
                  title={t('emptyState.textData')}
                  text={t('emptyState.createNewProjectInteract')}
                />
              )}

              {!loading && (
                <div className="tab__cards">
                  {filteredIndividualProjects?.map(p => (
                    <GroupProjectCard
                      data={p}
                      id={p.id}
                      group={false}
                      individual={true}
                      personalized={false}
                      deleteIndividualProject={deleteIndividualProject}
                    />
                  ))}
                </div>
              )}
            </DashboardContent>
          )}
        </Dashboard>
      ) : (
        <Error404 />
      )}
    </ProjectsProvider>
  );
}
